import { AuthenticationResult } from "@azure/msal-browser";
import logger from "@cayo/shared/libs/logger";
import LiteEvent from "@cayo/shared/settings/LiteEvent";
import SettingsStore from "@cayo/shared/settings/SettingsStore";
import AccessToken from "@cayo/shared/types/AccessToken";

const SSO_ACCESS_TOKEN_KEY = "access_token";
const SSO_ID_TOKEN_KEY = "id_token";

class TokenStorage {
  private readonly onSave = new LiteEvent<AccessToken | undefined>();
  readonly key = "token";
  private readonly log = logger.getLogger("TokenStorage");

  public get SaveEvent() {
    return this.onSave.expose();
  }

  constructor() {
    this.log.debug("constructor");
  }

  getToken(): AccessToken | undefined {
    const token = this._getSsoToken();
    if (token) {
      return token;
    }

    return this._getStoredToken();
  }

  public get accessToken(): string | undefined {
    return this.getToken()?.access_token;
  }

  setToken(token: AccessToken | undefined, saveToLocal?: boolean | undefined) {
    let keepInLocalStorage = !!this.localAuthSetting.get()?.access_token;
    if (saveToLocal !== undefined) {
      keepInLocalStorage = saveToLocal;
    }

    keepInLocalStorage ? this.localAuthSetting.set(token) : this.sessionAuthSetting.set(token);

    this.onSave.trigger(token);
  }

  setSsoToken(token: AuthenticationResult) {
    sessionStorage.setItem(SSO_ACCESS_TOKEN_KEY, token.accessToken);
    sessionStorage.setItem(SSO_ID_TOKEN_KEY, token.idToken);
    this.onSave.trigger({ access_token: token.accessToken });
  }

  clear() {
    sessionStorage.removeItem(SSO_ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(SSO_ID_TOKEN_KEY);
    this.localAuthSetting.set(undefined);
    this.sessionAuthSetting.set(undefined);

    this.onSave.trigger(undefined);
  }

  private _getSsoToken(): AccessToken | undefined {
    const accessToken = sessionStorage.getItem(SSO_ACCESS_TOKEN_KEY);
    const idToken = sessionStorage.getItem(SSO_ID_TOKEN_KEY) || undefined;
    return accessToken ? { access_token: accessToken, id_token: idToken } : undefined;
  }

  private _getStoredToken(): AccessToken | undefined {
    return this.localAuthSetting.get() || this.sessionAuthSetting.get();
  }

  public hasRefreshToken() {
    return !!this._getStoredToken()?.refresh_token;
  }

  isInLocalStorage() {
    return !!this.localAuthSetting.get();
  }

  private localAuthSetting = new SettingsStore<AccessToken, {}>("token", localStorage);
  private sessionAuthSetting = new SettingsStore<AccessToken, {}>("token", sessionStorage);
}

const tokenStorage = new TokenStorage();

export type ITokenStorage = typeof tokenStorage;
export default tokenStorage;
